import React, { Suspense, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { AlertTriangle, Lock } from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { TimelinePost } from '../types/post';
import { cn } from '../util/designUtil';
import { isMediaExpired, pickTimelineMedia, readMediaKind } from '../util/postMedia';
import { formatSubscribeCta } from '../util/subscriptionPrice';

// Code-split: Plyr and its stylesheet only reach a browser that actually has a
// video to play.
const VideoPlayer = React.lazy(() => import('./VideoPlayer'));

interface TimelinePostCardProps {
  post: TimelinePost;
  /** The creator's monthly price, or null when they are not taking subscriptions. */
  priceCredits: number | null;
  subscribing: boolean;
  onSubscribe: () => void;
  /** Re-reads page 1 so an expired signed URL is replaced with a fresh one. */
  onRefreshPost: () => void;
  refreshing: boolean;
}

/** Posted date, or nothing at all when the backend had no timestamp to give. */
function postedAt(createdAt: string | null): string | null {
  if (!createdAt) {
    return null;
  }
  const parsed = Date.parse(createdAt);
  return Number.isNaN(parsed) ? null : format(new Date(parsed), 'MMMM d, yyyy');
}

/**
 * One post in a creator's timeline: the image or video and the caption when the
 * viewer has access, and otherwise a placeholder that says a post exists
 * without leaking any of it. The locked branch carries no `src`, no `url()` and
 * no `background-image` — the blur is a CSS gradient — so the browser never
 * requests the hidden media, and no player is ever constructed for it.
 */
const TimelinePostCard: React.FC<TimelinePostCardProps> = ({
  post,
  priceCredits,
  subscribing,
  onSubscribe,
  onRefreshPost,
  refreshing,
}) => {
  const { theme } = useColorTheme();
  const posted = postedAt(post.createdAt);

  // Declared above the locked early return: hooks cannot live behind it. A
  // locked post has no media to pick, so nothing is read from it here either.
  const item = post.locked ? null : pickTimelineMedia(post.media);
  const kind = item ? readMediaKind(item) : null;
  const [playerFailed, setPlayerFailed] = useState(false);

  // A refreshed post arrives freshly signed, so a failure against the old URL
  // must not stick to it. The expiry is watched as well as the URL: a backend
  // that re-signs to the same URL still moves the expiry, and the notice has to
  // clear either way.
  useEffect(() => {
    setPlayerFailed(false);
  }, [item?.url, item?.expiresAt]);

  if (post.locked) {
    const count = post.mediaCount;

    return (
      <article className="w-full max-w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="relative w-full aspect-square overflow-hidden bg-gray-200">
          {/* Decorative blur, not a blurred copy of the image: these are plain
              background colours, so there is no background-image and no url()
              for the browser to fetch. */}
          <div
            aria-hidden="true"
            className="absolute -left-8 -top-8 h-2/3 w-2/3 rounded-full bg-gray-300 blur-2xl"
          />
          <div
            aria-hidden="true"
            className="absolute -right-10 top-1/4 h-2/3 w-2/3 rounded-full bg-gray-400 opacity-70 blur-2xl"
          />
          <div
            aria-hidden="true"
            className="absolute bottom-0 left-1/4 h-1/2 w-1/2 rounded-full bg-gray-300 opacity-80 blur-2xl"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
            <div className="rounded-full bg-white/80 p-3 shadow-sm">
              <Lock className={cn('h-6 w-6', theme.secondary)} />
            </div>
            <p className="text-sm font-semibold text-gray-900">Subscribers only</p>
            <p className="text-xs text-gray-700">
              {count} {count === 1 ? 'photo' : 'photos'}
            </p>
          </div>
        </div>

        <div className="p-4 space-y-2">
          {priceCredits !== null && (
            <button
              type="button"
              onClick={onSubscribe}
              disabled={subscribing}
              className={cn(
                'w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50',
                theme.accent,
              )}
            >
              <Lock className="h-4 w-4" />
              {formatSubscribeCta(priceCredits)}
            </button>
          )}
          {posted && <p className="text-xs text-gray-500">{posted}</p>}
        </div>
      </article>
    );
  }

  // A video URL that has already lapsed, or one the browser has just failed on,
  // gets the notice instead of a player: a dead <video> would only ever show a
  // broken control bar.
  const videoExpired =
    item !== null &&
    kind === 'video' &&
    (playerFailed || isMediaExpired(item.expiresAt, new Date()));

  return (
    <article className="w-full max-w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {item && kind === 'image' && (
        <img
          src={item.url}
          alt={post.caption || 'Post'}
          loading="lazy"
          className="w-full max-w-full h-auto block"
        />
      )}

      {item && kind === 'video' && videoExpired && (
        <div className="w-full max-w-full aspect-video bg-gray-900 flex flex-col items-center justify-center gap-2 p-4 text-center">
          <AlertTriangle className="h-6 w-6 text-gray-300" aria-hidden="true" />
          <p className="text-sm text-gray-100">This video expired — refresh to keep watching.</p>
          <button
            type="button"
            onClick={onRefreshPost}
            disabled={refreshing}
            className={cn(
              'inline-flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md text-sm font-semibold disabled:opacity-50',
              theme.accent,
            )}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      )}

      {item && kind === 'video' && !videoExpired && (
        <Suspense
          fallback={
            <div className="w-full max-w-full aspect-video bg-gray-100 animate-pulse" aria-hidden="true" />
          }
        >
          <VideoPlayer
            key={item.url}
            src={item.url}
            contentType={item.contentType}
            label={post.caption || 'Video post'}
            onError={() => setPlayerFailed(true)}
          />
        </Suspense>
      )}

      <div className="p-4 space-y-2">
        {post.caption.trim() !== '' && (
          <p className="text-sm text-gray-900 break-words whitespace-pre-wrap">{post.caption}</p>
        )}
        {posted && <p className="text-xs text-gray-500">{posted}</p>}
      </div>
    </article>
  );
};

export default TimelinePostCard;
